import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { ConfigProvider, App as AntApp } from 'antd';
import { theme } from '@/theme';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { Amplify } from 'aws-amplify';
import { useAuthStore } from '@/store/authStore';
import { extractCognitoErrorMessage, isServiceAccountRejectionError, formatCognitoErrorForDisplay } from '@/utils/cognitoErrors';
import MainLayout from '@/components/layout/MainLayout';
import ProtectedRoute from '@/components/auth/ProtectedRoute';
import PermissionsUnavailable from '@/components/auth/PermissionsUnavailable';
import LoginPage from '@/pages/LoginPage';
import VendorsPage from '@/pages/VendorsPage';
import VendorDetailPage from '@/pages/VendorDetailPage';
import VendorCreatePage from '@/pages/VendorCreatePage';
import AdHocTransferPage from '@/pages/AdHocTransferPage';
import OperatorVendorsPage from '@/pages/OperatorVendorsPage';
import OperatorVendorDetailPage from '@/pages/OperatorVendorDetailPage';
import TransfersPage from '@/pages/TransfersPage';
import HomeOverviewPage from '@/pages/HomeOverviewPage';
import TransferDetailPage from '@/pages/TransferDetailPage';
import AdminRoute from '@/components/auth/AdminRoute';
import CreateVendorRoute from '@/components/auth/CreateVendorRoute';
import SettingsRoute from '@/components/auth/SettingsRoute';
import TagsRoute from '@/components/auth/TagsRoute';
import OperatorRoute from '@/components/auth/OperatorRoute';
import OperatorOnlyRoute from '@/components/auth/OperatorOnlyRoute';
import TransfersRoute from '@/components/auth/TransfersRoute';
import BusinessUnitSettingsPage from '@/pages/BusinessUnitSettingsPage';
import BusinessUnitTagsPage from '@/pages/BusinessUnitTagsPage';
import './App.css';

// Component to handle OAuth callback
const OAuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const { checkAuth } = useAuthStore();
  const [status, setStatus] = useState<string>('Completing sign in...');

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Check if we have an authorization code in the URL
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const error = urlParams.get('error');
        const errorDescription = urlParams.get('error_description');
        
        if (error) {
          console.error('OAuth callback: Error from Cognito:', {
            error,
            errorDescription,
            allParams: Object.fromEntries(urlParams.entries()),
          });
          
          // Create error object from URL parameters
          const oauthError = {
            message: errorDescription || error,
            __type: error,
            name: error,
          };
          
          // Check if this is a service account rejection
          const isServiceAccount = isServiceAccountRejectionError(oauthError);
          const errorMessage = formatCognitoErrorForDisplay(oauthError);
          
          // If it's a service account rejection, handle it specially
          if (isServiceAccount) {
            const serviceAccountError = {
              code: 'SERVICE_ACCOUNT_NOT_ALLOWED',
              message: errorMessage,
              details: { error, errorDescription, oauthError },
              timestamp: new Date().toISOString(),
            };
            
            // Save to sessionStorage for display after redirect
            try {
              sessionStorage.setItem('serviceAccountError', JSON.stringify(serviceAccountError));
            } catch (e) {
              console.warn('Failed to save service account error to sessionStorage:', e);
            }
            
            // Set error in store
            useAuthStore.setState({
              user: null,
              isAuthenticated: false,
              isLoading: false,
              currentBusinessUnit: null,
              error: serviceAccountError,
            });
          } else {
            // Other OAuth errors - set in store for display
            useAuthStore.setState({
              error: {
                code: 'OAUTH_ERROR',
                message: errorMessage,
                details: { error, errorDescription },
                timestamp: new Date().toISOString(),
              },
            });
          }
          
          setStatus('Redirecting to login...');
          setTimeout(() => {
            navigate('/login', { replace: true });
          }, 800);
          return;
        }
        
        if (!code) {
          console.error('OAuth callback: No authorization code in URL');
          setStatus('Redirecting to login...');
          // Quickly redirect
          setTimeout(() => {
            navigate('/login', { replace: true });
          }, 800);
          return;
        }
        
        // Amplify should already be configured at app startup (in src/utils/amplify.ts)
        // Just verify it's configured - no need to reconfigure
        const currentConfig: any = Amplify.getConfig();
        if (!currentConfig.Auth?.Cognito?.userPoolId || !currentConfig.Auth?.Cognito?.userPoolClientId) {
          console.error('OAuth callback: Amplify is not configured');
          throw new Error('Amplify is not configured. Please check your configuration.');
        }
        
        // Verify OAuth config exists (check both possible locations for compatibility)
        const oauthConfig = currentConfig.Auth?.Cognito?.loginWith?.oauth || currentConfig.Auth?.oauth;
        if (!oauthConfig) {
          console.error('OAuth callback: OAuth config not found in Amplify');
          throw new Error('OAuth is not configured in Amplify');
        }
        
        // Check if this is a manual OAuth flow (from loginWithAzureAD)
        const useManualFlow = sessionStorage.getItem('useManualOAuthFlow') === 'true';
        
        let session;
        
        if (useManualFlow) {
          // Manual flow: We need to exchange the code manually since we bypassed Amplify's state
          // However, this is complex because we'd need PKCE codes. Instead, let's try using
          // Amplify's fetchAuthSession which might still work if the code is in the URL
          console.log('OAuth callback: Detected manual OAuth flow, attempting token exchange...');
          
          // Clean up the manual flow flag
          sessionStorage.removeItem('useManualOAuthFlow');
          
          // Try to use Amplify's fetchAuthSession - it might work if Cognito accepts the code
          try {
            session = await fetchAuthSession({ forceRefresh: true });
            if (!session.tokens) {
              // If Amplify can't handle it, we need to manually exchange
              throw new Error('Amplify could not exchange code - manual flow requires different approach');
            }
          } catch (err: any) {
            console.error('OAuth callback: Manual flow token exchange failed:', err);
            console.error('Solution: Use signInWithRedirect instead of manual URL construction');
            
            // Set error in store
            useAuthStore.setState({
              error: {
                code: 'OAUTH_EXCHANGE_ERROR',
                message: 'Authentication failed. Please try using the SSO redirect button instead.',
                details: { 
                  error: err,
                  note: 'Manual OAuth URL construction bypasses Amplify\'s PKCE state management. Use signInWithRedirect for proper code exchange.'
                },
                timestamp: new Date().toISOString(),
              },
            });
            
            setStatus('Redirecting to login...');
            setTimeout(() => {
              navigate('/login', { replace: true });
            }, 2000);
            return;
          }
        } else {
          // Standard Amplify flow: Use fetchAuthSession which handles code exchange automatically
          let exchangeAttempts = 0;
          const maxExchangeAttempts = 5;
          
          // Wait a moment for Amplify to initialize
          await new Promise(resolve => setTimeout(resolve, 200));
          
          while (exchangeAttempts < maxExchangeAttempts) {
            try {
              // Force refresh to trigger the OAuth callback handling
              session = await fetchAuthSession({ forceRefresh: true });
              
              if (session.tokens) {
                break;
              }
            } catch (err: any) {
              // Log errors for debugging
              if (exchangeAttempts >= maxExchangeAttempts - 1) {
                console.error('OAuth callback: Token exchange failed:', err);
                console.error('Error details:', {
                  name: err?.name,
                  message: err?.message,
                  code: err?.code,
                  underlyingError: err?.underlyingError,
                });
              }
            }
            
            exchangeAttempts++;
            if (exchangeAttempts < maxExchangeAttempts && !session?.tokens) {
              // Wait between attempts
              await new Promise(resolve => setTimeout(resolve, 500));
            }
          }
          
          if (!session?.tokens) {
            console.error('OAuth callback: Failed to exchange authorization code for tokens');
            console.error('Possible causes:');
            console.error('1. Authorization code expired (codes are single-use and time-limited)');
            console.error('2. PKCE code verifier mismatch (if using PKCE)');
            console.error('3. Redirect URI mismatch');
            console.error('4. Invalid client configuration');
            
            // Set error in store
            useAuthStore.setState({
              error: {
                code: 'OAUTH_EXCHANGE_ERROR',
                message: 'Failed to complete authentication. Please try logging in again.',
                details: { 
                  code: code ? 'present' : 'missing',
                  urlParams: Object.fromEntries(urlParams.entries()),
                },
                timestamp: new Date().toISOString(),
              },
            });
            
            setStatus('Redirecting to login...');
            setTimeout(() => {
              navigate('/login', { replace: true });
            }, 2000);
            return;
          }
        }
        
        // CRITICAL: Validate email BEFORE proceeding with authentication
        // This prevents service accounts from being authenticated and creating user records
        const idTokenPayload = session.tokens?.idToken?.payload || {};
        const email = (idTokenPayload.email as string) || '';
        // Note: Azure AD does NOT provide email_verified claim
        // Default to true for federated Azure AD users (managed by admins)
        const emailVerified = idTokenPayload.email_verified as boolean | undefined ?? true;
        
        // Log email verification status for debugging
        if (import.meta.env.DEV || import.meta.env.VITE_DEBUG_CONFIG) {
          console.log('OAuth callback: Email verification status:', {
            email,
            emailVerified,
            emailVerifiedInToken: idTokenPayload.email_verified !== undefined,
            note: idTokenPayload.email_verified === undefined 
              ? 'email_verified not in token (Azure AD limitation) - defaulting to true'
              : 'email_verified in token (from Cognito Lambda trigger)'
          });
        }
        
        // Service account email validation function (matches authStore validation)
        const isServiceAccountEmail = (email: string): boolean => {
          if (!email) return false;
          const normalizedEmail = email.toLowerCase().trim();
          const serviceAccountPatterns = [
            /^.+?-ad@tnc\.org$/i,
            /^.+?-aws@tnc\.org$/i,
            /^.+?-hr@tnc\.org$/i,
            /^.+?-az@tnc\.org$/i,
          ];
          return serviceAccountPatterns.some(pattern => pattern.test(normalizedEmail));
        };
        
        // Validate email immediately - reject service accounts before any further processing
        if (email && isServiceAccountEmail(email)) {
          console.error(`OAuth callback: Service account email detected and rejected: ${email}`);
          
          // Sign out immediately to prevent further authentication
          try {
            await signOut();
            console.log('OAuth callback: Service account signed out successfully');
          } catch (signOutError) {
            // Extract error message robustly in case signOut fails
            const signOutErrorMessage = extractCognitoErrorMessage(
              signOutError,
              'Failed to sign out service account'
            );
            console.error('OAuth callback: Failed to sign out service account:', {
              error: signOutError,
              message: signOutErrorMessage,
            });
            // Continue anyway - the validation and error setting will still work
          }
          
          // Create error object
          const serviceAccountError = {
            code: 'SERVICE_ACCOUNT_NOT_ALLOWED',
            message: 'Service account logins are not permitted. Please use your standard TNC login email address.',
            details: { email },
            timestamp: new Date().toISOString(),
          };
          
          // Persist error to sessionStorage for display after redirect
          try {
            sessionStorage.setItem('serviceAccountError', JSON.stringify(serviceAccountError));
            console.log('OAuth callback: Service account error saved to sessionStorage');
          } catch (e) {
            console.warn('OAuth callback: Failed to save service account error to sessionStorage:', e);
          }
          
          // Set error in store for display on login page
          useAuthStore.setState({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            currentBusinessUnit: null,
            error: serviceAccountError,
          });
          
          console.error('OAuth callback: Service account rejected. Error set in store:', {
            code: 'SERVICE_ACCOUNT_NOT_ALLOWED',
            email,
          });
          
          // Clean up the URL and redirect to login with error
          window.history.replaceState({}, document.title, window.location.pathname);
          setStatus('Redirecting to login...');
          
          // Redirect immediately - error is in store and sessionStorage
          navigate('/login', { replace: true });
          return;
        }
        
        // Clean up the URL immediately to prevent reloads
        window.history.replaceState({}, document.title, window.location.pathname);
        
        // Now call checkAuth to get user info and set up the auth state
        // Email is already validated above, but checkAuth will validate again as a safety check
        try {
          await checkAuth();
        } catch (err: any) {
          // Extract error message robustly
          const errorMessage = formatCognitoErrorForDisplay(err);
          
          // Check if it's a service account rejection using robust detection
          if (isServiceAccountRejectionError(err) || 
              errorMessage.toLowerCase().includes('service account') ||
              errorMessage.toLowerCase().includes('standard tnc login')) {
            console.error('OAuth callback: Service account rejected:', {
              error: err,
              message: errorMessage,
            });
            // Error should already be set in store by checkAuth, but ensure it's there
            const storeState = useAuthStore.getState();
            if (!storeState.error || storeState.error.code !== 'SERVICE_ACCOUNT_NOT_ALLOWED') {
              const serviceAccountError = {
                code: 'SERVICE_ACCOUNT_NOT_ALLOWED',
                message: errorMessage,
                details: { originalError: err },
                timestamp: new Date().toISOString(),
              };
              useAuthStore.setState({ error: serviceAccountError });
            }
            setStatus('Redirecting to login...');
            setTimeout(() => {
              navigate('/login', { replace: true });
            }, 500);
            return;
          }
          // Other errors - extract and log for debugging
          console.error('OAuth callback: checkAuth failed:', {
            error: err,
            message: errorMessage,
            fullError: err,
          });
        }
        
        // Check if authentication succeeded (don't wait for store to update - it should be immediate)
        const storeState = useAuthStore.getState();
        if (storeState.isAuthenticated && storeState.user) {
          // Immediately redirect - don't show status messages
          navigate('/', { replace: true });
        } else {
          // Auth failed - redirect to login (error should already be in store if it's a service account)
          setStatus('Redirecting to login...');
          setTimeout(() => {
            navigate('/login', { replace: true });
          }, 1000);
        }
      } catch (error: any) {
        // Extract error message robustly
        const errorMessage = formatCognitoErrorForDisplay(error);
        console.error('OAuth callback: Unexpected error:', {
          error,
          message: errorMessage,
          fullError: error,
        });
        
        // Set error in store for display
        useAuthStore.setState({
          error: {
            code: 'OAUTH_CALLBACK_ERROR',
            message: errorMessage,
            details: { originalError: error },
            timestamp: new Date().toISOString(),
          },
        });
        
        setStatus('Redirecting to login...');
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 1000);
      }
    };

    handleCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column',
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '100vh',
      gap: '16px',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    }}>
      <div style={{
        width: '40px', 
        height: '40px', 
        border: '4px solid rgba(255, 255, 255, 0.3)',
        borderTop: '4px solid #fff',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
      }} />
      <div style={{ 
        fontSize: '16px', 
        color: '#fff',
        fontWeight: 500
      }}>
        {status}
      </div>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

// Inner component that handles auth checks (must be inside Router)
const AppRoutes: React.FC = () => {
  const { checkAuth, isLoading, authorizationStatus } = useAuthStore();
  const location = useLocation();

  useEffect(() => {
    // Once per page load. A later navigation does not call this again.
    // /oauth2/callback calls checkAuth itself after the code exchange.
    if (location.pathname === '/oauth2/callback') {
      return;
    }

    const runAuthCheck = async () => {
      try {
        await checkAuth();
      } catch (error) {
        console.error('Auth check failed:', error);
      }
    };
    runAuthCheck();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Only show loading spinner if we're not on OAuth callback or login page
  // OAuth callback has its own loading state
  if (isLoading && location.pathname !== '/oauth2/callback' && location.pathname !== '/login') {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}>
        <div style={{ 
          textAlign: 'center',
          color: '#fff'
        }}>
          <div style={{ 
            width: '40px', 
            height: '40px', 
            border: '4px solid rgba(255,255,255,0.3)',
            borderTop: '4px solid #fff',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <div>Loading Data Exchange...</div>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/oauth2/callback" element={<OAuthCallback />} />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            {authorizationStatus === 'unavailable' ? (
              <PermissionsUnavailable />
            ) : (
            <MainLayout>
                <Routes>
                  {/* Landing page. */}
                  <Route path="/" element={<HomeOverviewPage />} />
                  <Route
                    path="/vendors"
                    element={
                      <AdminRoute>
                        <VendorsPage />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="/vendors/new"
                    element={
                      <CreateVendorRoute>
                        <VendorCreatePage />
                      </CreateVendorRoute>
                    }
                  />
                  <Route
                    path="/settings/business-units"
                    element={
                      <SettingsRoute>
                        <BusinessUnitSettingsPage />
                      </SettingsRoute>
                    }
                  />
                  <Route
                    path="/settings/tags"
                    element={
                      <TagsRoute>
                        <BusinessUnitTagsPage />
                      </TagsRoute>
                    }
                  />
                  <Route
                    path="/vendors/:vendorId"
                    element={
                      <AdminRoute>
                        <VendorDetailPage />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="/transfers/ad-hoc"
                    element={
                      <OperatorRoute>
                        <AdHocTransferPage />
                      </OperatorRoute>
                    }
                  />
                  <Route
                    path="/transfers"
                    element={
                      <TransfersRoute>
                        <TransfersPage />
                      </TransfersRoute>
                    }
                  />
                  <Route
                    path="/transfers/detail/*"
                    element={
                      <TransfersRoute>
                        <TransferDetailPage />
                      </TransfersRoute>
                    }
                  />
                  <Route
                    path="/my-vendors"
                    element={
                      <OperatorOnlyRoute>
                        <OperatorVendorsPage />
                      </OperatorOnlyRoute>
                    }
                  />
                  <Route
                    path="/my-vendors/:vendorId"
                    element={
                      <OperatorOnlyRoute>
                        <OperatorVendorDetailPage />
                      </OperatorOnlyRoute>
                    }
                  />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </MainLayout>
            )}
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <ConfigProvider theme={theme}>
      <AntApp>
        <Router>
          <AppRoutes />
        </Router>
      </AntApp>
    </ConfigProvider>
  );
};

export default App;
